import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import _ from 'lodash'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import clsx from 'clsx'
import {
  getEditorExtensions,
  serializeRichTextSliceForClipboard,
} from '../../richtext'
import { expandEmptyParagraphMarkers } from '../../../../../utils/multilineText'
import { MarkdownType } from '../../multiline-text/MultilineTextEditor/types'
import './styles.scss'

type Props = {
  text: string
  markdownType?: MarkdownType
}

export const MarkdownPreview = ({ text, markdownType }: Props) => {
  // editorProps は editor 生成前に定義されるため、ref 経由で自身の editor を参照する
  const editorRef = useRef<Editor | null>(null)
  const editor = useEditor({
    extensions: getEditorExtensions(),
    editable: false,
    content: '',
    parseOptions: {
      preserveWhitespace: 'full',
    },
    editorProps: {
      attributes: {
        class: clsx('richtext-editor', {
          'markdown-side-by-side': markdownType === MarkdownType.SideBySide,
        }),
      },
    },
  })
  editorRef.current = editor

  // editable: false のエディタでは ProseMirror が DOM 選択を追跡しないため、
  // copy はブラウザ既定処理（段落間を "\n\n" で連結）になり、空段落が
  // Markdown ソースエディタへのペーストで空行3つに増殖してしまう。
  // また、フォーカスを持たないプレビューでは copy イベントは body 側で発火するため、
  // document レベルで捕捉し、選択範囲がこのエディタ内のときだけ
  // ProseMirror の位置に写像して Markdown ソースとしてコピーする。
  const handleCopy = useCallback((event: ClipboardEvent) => {
    const ed = editorRef.current
    const selection = window.getSelection()
    if (!ed || !event.clipboardData || !selection || selection.isCollapsed) {
      return
    }
    const range = selection.getRangeAt(0)
    const editorRange = document.createRange()
    editorRange.selectNodeContents(ed.view.dom)
    // 選択がこのエディタと交差しない場合は既定処理に任せる
    if (
      range.compareBoundaryPoints(Range.START_TO_END, editorRange) <= 0 ||
      range.compareBoundaryPoints(Range.END_TO_START, editorRange) >= 0
    ) {
      return
    }
    // 全選択等でエディタの外側から始まる/終わる選択は、エディタ範囲にクランプする
    const startsBefore =
      range.compareBoundaryPoints(Range.START_TO_START, editorRange) < 0
    const endsAfter =
      range.compareBoundaryPoints(Range.END_TO_END, editorRange) > 0
    const start = startsBefore
      ? 0
      : ed.view.posAtDOM(range.startContainer, range.startOffset)
    const end = endsAfter
      ? ed.state.doc.content.size
      : ed.view.posAtDOM(range.endContainer, range.endOffset)
    if (start < 0 || end < 0 || start === end) return
    const slice = ed.state.doc.slice(Math.min(start, end), Math.max(start, end))
    // リッチエディタへのペースト用に、レンダー済み HTML も既定処理と同様に書いておく
    const clampedRange = range.cloneRange()
    if (startsBefore) {
      clampedRange.setStart(editorRange.startContainer, editorRange.startOffset)
    }
    if (endsAfter) {
      clampedRange.setEnd(editorRange.endContainer, editorRange.endOffset)
    }
    const html = document.createElement('div')
    html.appendChild(clampedRange.cloneContents())
    event.clipboardData.setData('text/html', html.innerHTML)
    event.clipboardData.setData(
      'text/plain',
      serializeRichTextSliceForClipboard(ed, slice)
    )
    event.preventDefault()
  }, [])

  useEffect(() => {
    document.addEventListener('copy', handleCopy, true)
    return () => document.removeEventListener('copy', handleCopy, true)
  }, [handleCopy])

  const debouncedSetContent = useMemo(
    () =>
      _.debounce((ed: Editor, md: string) => {
        ed.commands.setContent(expandEmptyParagraphMarkers(md || ''), {
          contentType: 'markdown',
          emitUpdate: false,
        })
      }, 200),
    []
  )

  useEffect(() => {
    if (!editor) return
    debouncedSetContent(editor, text)
    return () => debouncedSetContent.cancel()
  }, [editor, text, debouncedSetContent])

  return <EditorContent editor={editor} />
}
